import { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { STEP_NAMES } from 'accounts/constants/steps';
import {
  getDisplayStatus,
  getStepPath,
  isStepUnHidden,
  sortStepsByGate,
} from 'accounts/utils/flow-steps';
import type { ProgressBarItems } from 'components/ProgressBar/ProgressBar';
import { STATUS, FlowStatus } from 'models/enums';

export function useStepFormLayout({
  currentStep,
  propertyId,
  unitId,
  stepsByGate,
}: {
  currentStep: any;
  propertyId: string;
  unitId: string;
  stepsByGate: Record<string, any[]>;
}) {
  const location = useLocation();
  const [progressItems, setProgressItems] = useState<ProgressBarItems[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentStep && propertyId) {
      setLoading(true);

      // `stepsByGate` is keyed by the gate of every step in the flow, and
      // `currentStep` came out of that same list, so its gate is always a key —
      // `getFilteredGateSteps` narrows the values, never drops a key.
      const currentStepsGate = stepsByGate[currentStep.gate]!.filter(
        isStepUnHidden,
      )
        .filter((step: any) => step.flowId === currentStep.flowId)
        .filter((step: any) =>
          [FlowStatus.Created, FlowStatus.InProgress].includes(step.flowStatus),
        );

      const residentExperienceSteps = currentStepsGate.filter(
        (actualStep: any) =>
          actualStep.name === STEP_NAMES.UnitResidentExperience,
      );
      const newProgressItems = sortStepsByGate(currentStepsGate).map(
        (step: any) => {
          const stepPath = getStepPath(step, propertyId, unitId);
          let label = step.displayName;

          if (step.name === STEP_NAMES.UnitResidentExperience) {
            if (residentExperienceSteps.length > 1) {
              label = `${label}<br />Unit ${step.unit?.basicInfo.unitNumber}`;
            }
          }

          const progressItem = {
            label,
            uncompleted:
              currentStep.uniqueId === step.uniqueId
                ? false
                : getDisplayStatus(step.status) === STATUS.INCOMPLETE,
            to: step.key !== 'w9' ? stepPath : location.pathname,
            active: stepPath === location.pathname,
          };

          return progressItem;
        },
      );

      setProgressItems(newProgressItems);
      setLoading(false);
    }
  }, [currentStep, propertyId, unitId, stepsByGate]);

  return { progressItems, loading };
}
